import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  FlagRounded
} from '@mui/icons-material';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, mediaBaseUrl, truncateText } from './FlowLayout';

const ManageTourType = () => {
  const { continentId } = useParams();
  const [tourTypes, setTourTypes] = useState([]);
  const [continentName, setContinentName] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tourTypeResponse, continentResponse] = await Promise.all([
          axios.get(`https://aaryanholidays.in/backend/continents/tour-types/${continentId}`),
          axios.get(`https://aaryanholidays.in/backend/continents/${continentId}`)
        ]);
        setTourTypes(tourTypeResponse.data);
        setContinentName(continentResponse.data.continent);
      } catch (error) {
        console.error('There was an error fetching the tour types!', error);
      }
    };

    fetchData();
  }, [continentId]);

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      axios.delete(`https://aaryanholidays.in/backend/tour-types/${id}`)
        .then(() => setTourTypes((current) => current.filter((type) => type.id !== id)))
        .catch((error) => console.error('There was an error deleting the tour type!', error));
    }
  };

  const filteredTourTypes = useMemo(() => {
    return tourTypes.filter((item) =>
      String(item.tour_type || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [tourTypes, search]);

  return (
    <Box>
      <FlowBreadcrumbs
        items={[
          { label: 'Tour Management', to: '/manage-continents' },
          { label: continentName || 'Continent' },
          { label: 'Countries' }
        ]}
      />

      <FlowPageHeader
        title={`${continentName || 'Continent'} Countries`}
        subtitle="Manage the second level of the travel structure with faster search, richer previews, and direct package access."
        search={search}
        searchPlaceholder="Search countries, headings, or descriptions"
        onSearchChange={setSearch}
        action={(
          <Button
            component={Link}
            to={`/manage-tour-type/add/${continentId}`}
            variant="contained"
            startIcon={<AddRounded />}
            sx={{ borderRadius: 999, px: 2.5, py: 1.2, textTransform: 'none', fontWeight: 700 }}
          >
            Add Country
          </Button>
        )}
      />

      <FlowStatGrid
        items={[
          { label: 'Countries', value: tourTypes.length, icon: FlagRounded, tone: 'linear-gradient(135deg, #0f766e 0%, #2dd4bf 100%)' }
        ]}
      />

      <SoftPanel>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Heading</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Banner</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTourTypes.map((type) => (
                <TableRow key={type.id} hover>
                  <TableCell>{type.id}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700 }}>{type.tour_type}</Typography>
                  </TableCell>
                  <TableCell>{truncateText(type.heading, 60)}</TableCell>
                  <TableCell>{truncateText(type.desc_txt, 110)}</TableCell>
                  <TableCell>
                    {type.banner_type === 'video' ? (
                      <video
                        src={`${mediaBaseUrl}${type.banner_src}`}
                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }}
                        controls
                      />
                    ) : (
                      <img
                        src={`${mediaBaseUrl}${type.banner_src}`}
                        alt={type.tour_type}
                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                      <Tooltip title="Edit country">
                        <IconButton component={Link} to={`/manage-tour-type/edit/${type.id}/${continentId}`}>
                          <EditRounded />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete country">
                        <IconButton onClick={() => handleDelete(type.id)}>
                          <DeleteOutlineRounded />
                        </IconButton>
                      </Tooltip>
                      <Button
                        component={Link}
                        to={`/manage-destination/${type.id}`}
                        variant="outlined"
                        sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                      >
                        Open Packages
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SoftPanel>
    </Box>
  );
};

export default ManageTourType;
